import { Card, Metric, Text } from "@tremor/react";
import type { TranslateFn } from "#/lib/i18n.js";

type PathogenData = {
  pathogenId: string;
  name: string;
  vector: string;
  incubationMinDays: number;
  incubationMaxDays: number;
  cfrMinPct: string;
  cfrMaxPct: string;
  vaccineStatus: string;
  h2hTransmission: string;
  locale: string;
  sourceUrl: string;
  verifiedAt: string;
  fetchedAt: string;
  localeFellBack: boolean;
};

type Props = {
  pathogen: PathogenData | null;
  locale: string;
  t: TranslateFn;
};

function Row({
  label,
  value,
  sourceUrl,
  locale,
  t,
}: {
  label: string;
  value: string;
  sourceUrl?: string;
  locale: string;
  t: TranslateFn;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        paddingBlock: "0.625rem",
        borderBlockEnd: "1px solid var(--color-border-subtle)",
      }}
    >
      <Text
        style={{
          color: "var(--color-fg-muted)",
          fontSize: "0.8125rem",
          fontWeight: 500,
          minInlineSize: "10ch",
        }}
      >
        {label}
      </Text>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
        }}
      >
        <Text
          style={{
            color: "var(--color-fg-default)",
            fontSize: "0.8125rem",
            fontWeight: 600,
            textAlign: "end",
          }}
        >
          {value}
        </Text>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("pathogen.sourceLink")}
            style={{
              color: "var(--color-accent-info)",
              fontSize: "0.75rem",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              transform: locale === "ar" ? "scaleX(-1)" : undefined,
            }}
          >
            ↗
          </a>
        )}
      </div>
    </div>
  );
}

export function PathogenReferenceCard({ pathogen, locale, t }: Props) {
  if (!pathogen) return null;

  const cardStyle: React.CSSProperties = {
    background: "var(--color-bg-surface)",
    border: "1px solid var(--color-border-default)",
    borderRadius: "0.5rem",
    padding: "1.25rem",
  };

  const rows = [
    {
      label: t("pathogen.vector"),
      value: pathogen.vector,
    },
    {
      label: t("pathogen.incubation"),
      value: t("pathogen.incubationRange", {
        min: pathogen.incubationMinDays,
        max: pathogen.incubationMaxDays,
      }),
    },
    {
      label: t("pathogen.cfr"),
      value: t("pathogen.cfrRange", {
        min: pathogen.cfrMinPct,
        max: pathogen.cfrMaxPct,
      }),
    },
    {
      label: t("pathogen.vaccine"),
      value: pathogen.vaccineStatus,
    },
    {
      label: t("pathogen.h2h"),
      value: pathogen.h2hTransmission,
    },
  ];

  return (
    <section aria-label={t("pathogen.title")} style={{ marginBlockStart: "1rem" }}>
      <Card decoration="top" decorationColor="amber" style={cardStyle}>
        <Metric
          style={{
            color: "var(--color-fg-default)",
            fontSize: "var(--fs-lg)",
            fontWeight: 600,
            marginBlockEnd: "0.75rem",
          }}
        >
          {t("pathogen.title")}
        </Metric>
        <div>
          {rows.map((row) => (
            <Row
              key={row.label}
              label={row.label}
              value={row.value}
              sourceUrl={pathogen.sourceUrl}
              locale={locale}
              t={t}
            />
          ))}
        </div>
        {pathogen.localeFellBack && (
          <Text
            style={{
              color: "var(--color-fg-muted)",
              fontSize: "0.6875rem",
              marginBlockStart: "0.5rem",
            }}
          >
            {t("pathogen.localeFallback")}
          </Text>
        )}
      </Card>
    </section>
  );
}
